import { Component, OnInit, Input, Output, EventEmitter, signal, computed, inject, ChangeDetectorRef } from '@angular/core';
import { Temporal } from '@js-temporal/polyfill';

export type DateSelectionType = 'single' | 'range';
export type TimeUnit = 'day' | 'week' | 'month';

@Component({
  selector: 'app-metro-date-picker',
  templateUrl: './metro-date-picker.component.html',
  standalone: false
})
export class MetroDatePickerComponent  {
  private cdRef = inject(ChangeDetectorRef);

  @Input()
  set modeInput(value: 'single' | 'range') {
    this.mode.set(value);
  }

  @Input()
  set selectedDateInput(value: Temporal.PlainDate | null) {
    this.selectedDate.set(value);
  }

  @Input()
  set startDateInput(value: Temporal.PlainDate | null) {
    this.startDate.set(value);
  }

  @Input()
  set endDateInput(value: Temporal.PlainDate | null) {
    this.endDate.set(value);
  }

  @Output() dateSelected = new EventEmitter<Temporal.PlainDate>();
  @Output() rangeSelected = new EventEmitter<{ start: Temporal.PlainDate; end: Temporal.PlainDate }>()

  monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  daysOfWeek = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  private defaultRangeDays = 7;

  disabled = signal(false);
  error = signal<string | null>(null);
  mode = signal<'single' | 'range'>('range');

  today = Temporal.Now.plainDateISO();
  currentMonth = signal(this.today.toPlainYearMonth());
  selectedDate = signal<Temporal.PlainDate>(this.today);
  startDate = signal<Temporal.PlainDate | null>(null);
  endDate = signal<Temporal.PlainDate | null>(null);

  hoverDate = signal<Temporal.PlainDate | null>(null);

  nextMonth = computed(() => this.currentMonth().add({ months: 1 }));


  constructor() {

  }

  getWeeks(month: Temporal.PlainYearMonth): Temporal.PlainDate[][] {
    const firstDay = month.toPlainDate({ day: 1 });
    const startDate = firstDay.subtract({ days: firstDay.dayOfWeek % 7 });

    return Array.from({ length: 6 }).map((_, weekIndex) =>
      Array.from({ length: 7 }).map((_, dayIndex) =>
        startDate.add({ days: weekIndex * 7 + dayIndex })
      )
    );
  }

  getCalendarDays(month: Temporal.PlainYearMonth): Temporal.PlainDate[] {
  const weeks = this.getWeeks(month);

  return weeks.reduce((accumulator, currentWeek) => accumulator.concat(currentWeek), []);
}

  prevMonth() {
    if (!this.disabled()) {
      this.currentMonth.update(m => m.subtract({ months: 1 }));
    }
  }

  advanceMonth() {
    if (!this.disabled()) {
      this.currentMonth.update(m => m.add({ months: 1 }));
    }
  }

  selectDate(date: Temporal.PlainDate, event: MouseEvent) {
    event.stopPropagation();
    if (this.disabled() || !this.isDateInVisibleMonths(date)) return;

    if (this.mode() === 'range') {
      const start = this.startDate();
      const end = this.endDate();

      if (!start || (start && end)) {
        this.startDate.set(date);
        this.selectedDate.set(date);
        this.endDate.set(null);
      } else if (Temporal.PlainDate.compare(date, start) < 0) {
        this.endDate.set(start);
        this.startDate.set(date);
      } else {
        this.endDate.set(date);
      }

      if (this.startDate() && this.endDate()) {
        this.rangeSelected.emit({ start: this.startDate()!, end: this.endDate()! });
      }
    } else {
      this.selectedDate.set(date);
      this.dateSelected.emit(date);
    }
  }


  isDateInVisibleMonths(date: Temporal.PlainDate): boolean {
    return this.isCurrentMonth(date, this.currentMonth()) ||
           this.isCurrentMonth(date, this.nextMonth());
  }

  isSelected(date: Temporal.PlainDate): boolean {
    return !!this.selectedDate() && date.equals(this.selectedDate()!);
  }

  isCurrentMonth(date: Temporal.PlainDate, month: Temporal.PlainYearMonth): boolean {
    return date.month === month.month && date.year === month.year;
  }

isInRange(date: Temporal.PlainDate): boolean {
  const start = this.startDate();
  const end = this.endDate();
  const hover = this.hoverDate();

  if (start && end) {
    return Temporal.PlainDate.compare(date, start) >= 0 &&
           Temporal.PlainDate.compare(date, end) <= 0;
  }

  if (start && hover && !end) {
    const isForward = Temporal.PlainDate.compare(hover, start) >= 0;
    if (isForward) {
      return Temporal.PlainDate.compare(date, start) >= 0 && Temporal.PlainDate.compare(date, hover) <= 0;
    } else {
      return Temporal.PlainDate.compare(date, hover) >= 0 && Temporal.PlainDate.compare(date, start) <= 0;
    }
  }

  return false;
}

  isRangeStart(date: Temporal.PlainDate): boolean {
    return this.mode() === 'range' &&
           this.startDate() !== null &&
           date.equals(this.startDate()!);
  }

  isRangeEnd(date: Temporal.PlainDate): boolean {
    return this.mode() === 'range' &&
           this.endDate() !== null &&
           date.equals(this.endDate()!);
  }

  isRangeEdge(date: Temporal.PlainDate): boolean {
    return this.isRangeStart(date) || this.isRangeEnd(date);
  }

  getMonthYearLabel(month: Temporal.PlainYearMonth): string {
    return `${this.monthNames[month.month - 1]} ${month.year}`;
  }

  onMouseEnter(date: Temporal.PlainDate) {
    if (this.mode() === 'range' && this.startDate() && !this.endDate()) {
      this.hoverDate.set(date);
    }
  }

  onMouseLeave() {
    this.hoverDate.set(null);
  }
}
